import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import PasswordToggleButton from '@/components/ui/PasswordToggleButton';
import { useAuth } from '@/contexts/AuthContext';
import Button from '@/components/Button';
import { getErrorMessage, getErrorSuggestion, getErrorCode } from '@/utils/errors';

// ---------------------------------------------------------------------------
// Google OAuth client ID — read from env at module load time.
// When unset (empty string), the Google button is NOT rendered and the GSI
// script is NOT injected. This keeps the feature dormant until provisioned.
// ---------------------------------------------------------------------------
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID ?? '';

const TAGLINES = [
  'Motion, decoded.',
  'Find the Pointe.\nTrace the Form.',
  'Train Smarter.\nMove Harder.',
  'Every movement matters.',
  'Movement, clarified.',
  'Movement into Mastery.',
];

export default function LoginPage() {
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [tagline] = useState(() => TAGLINES[Math.floor(Math.random() * TAGLINES.length)]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorSuggestion, setErrorSuggestion] = useState<string | undefined>(undefined);
  // googleError is used for account_not_found so we can show the request-access link
  const [googleErrorCode, setGoogleErrorCode] = useState<string | null>(null);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const googleButtonRef = useRef<HTMLDivElement>(null);

  // ---------------------------------------------------------------------------
  // GSI script injection + button rendering.
  // DORMANT-SAFE: only runs when GOOGLE_CLIENT_ID is non-empty.
  // When GOOGLE_CLIENT_ID is unset, no <script> is injected, no GSI global is
  // accessed, and the Google button section is NOT rendered — the password login
  // is byte-equivalent to what it was before this change.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return; // dormant — don't load GSI at all

    const scriptId = 'gsi-client';

    function initializeGsi() {
      if (!window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
        cancel_on_tap_outside: true,
      });
      // Render the button into the ref div if it exists.
      if (googleButtonRef.current) {
        window.google.accounts.id.renderButton(googleButtonRef.current, {
          type: 'standard',
          theme: 'filled_black',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: 400,
        });
      }
    }

    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGsi;
      document.head.appendChild(script);
    } else if (window.google?.accounts?.id) {
      // Script already loaded (e.g. hot-reload in dev)
      initializeGsi();
    }

    // No cleanup needed — the GSI script has no cleanup API and must persist
    // across component remounts.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoogleCredential = async (response: { credential: string }) => {
    setGoogleErrorCode(null);
    setError(null);
    setErrorSuggestion(undefined);
    setIsGoogleSubmitting(true);

    try {
      await loginWithGoogle(response.credential);
      navigate('/', { replace: true });
    } catch (err) {
      const code = getErrorCode(err);
      setGoogleErrorCode(code ?? null);

      // Map backend error codes → UI messages.
      // These match the EXACT error strings from auth_routes.py google_callback().
      if (code === 'account_not_found') {
        setError(
          'No PointeTracer account matches that Google email. If you already ' +
            'have an account, sign in with your email and password below — your ' +
            'account may use a different email address.'
        );
        // A secondary "Don't have an account yet? Request access" link renders
        // below (see googleErrorCode check) for genuinely-new users.
      } else if (code === 'invalid_google_token') {
        setError("Couldn't verify your Google sign-in — please try again.");
      } else if (code === 'google_email_not_verified') {
        setError('Your Google account email is not verified. Please verify it with Google and try again.');
      } else if (code === 'google_account_mismatch') {
        setError('This PointeTracer account is already linked to a different Google account.');
      } else if (code === 'google_account_conflict') {
        setError('This Google account is already linked to another PointeTracer account.');
      } else if (code === 'account_inactive') {
        setError('This account has been deactivated. Contact support if you believe this is an error.');
      } else if (code === 'oauth_not_configured') {
        setError('Google Sign-In is not available on this server right now.');
      } else {
        setError(getErrorMessage(err, 'Google sign-in failed. Please try again.'));
      }
    } finally {
      setIsGoogleSubmitting(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setErrorSuggestion(undefined);
    setGoogleErrorCode(null);
    setIsSubmitting(true);

    try {
      await login(email, password);
      navigate('/', { replace: true });
    } catch (err) {
      setError(getErrorMessage(err));
      setErrorSuggestion(getErrorSuggestion(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes mc-fade-up {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .mc-login-form {
          animation: mc-fade-up 0.45s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .mc-input-wrap input:focus {
          outline: none;
          border-color: var(--color-brand-primary);
          box-shadow: 0 0 0 2px var(--color-brand-primary);
        }
        .mc-submit-btn {
          transition: background-color 150ms ease, box-shadow 150ms ease, transform 100ms ease;
        }
        .mc-submit-btn:not(:disabled):hover {
          background-color: var(--color-brand-primary-hover);
          box-shadow: 0 0 18px rgba(59, 130, 246, 0.30), 0 4px 12px rgba(0, 0, 0, 0.20);
        }
        .mc-submit-btn:not(:disabled):active {
          transform: scale(0.98);
        }
        .mc-forgot-link {
          color: var(--color-text-muted);
          text-decoration: none;
          font-size: 13px;
          transition: color 150ms ease;
        }
        .mc-forgot-link:hover {
          color: var(--color-text-secondary);
        }
        .mc-invite-link {
          color: var(--color-brand-primary);
          text-decoration: none;
          font-weight: 500;
          transition: color 150ms ease;
        }
        .mc-invite-link:hover {
          color: var(--color-brand-primary-hover);
        }
      `}</style>

      {/* Full-screen split layout */}
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          backgroundColor: 'var(--color-surface-base)',
        }}
      >
        {/* ── Left panel (desktop only) ─────────────────────────────────────── */}
        <div
          className="hidden lg:flex"
          style={{
            width: '50%',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: 'var(--color-surface-base)',
            padding: '64px 56px',
          }}
        >
          {/* Radial brand-glow anchored upper-right */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: '-80px',
              right: '-80px',
              width: '560px',
              height: '560px',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(59,130,246,0.18) 0%, rgba(59,130,246,0.06) 45%, transparent 70%)',
              pointerEvents: 'none',
            }}
          />
          {/* Secondary soft glow, lower-left */}
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              bottom: '-60px',
              left: '-40px',
              width: '360px',
              height: '360px',
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(59,130,246,0.07) 0%, transparent 65%)',
              pointerEvents: 'none',
            }}
          />

          {/* Left panel content */}
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: '28px',
              maxWidth: '380px',
            }}
          >
            {/* Logo mark — drop-shadow (alpha-aware) instead of box-shadow
                so the glow follows the running-man silhouette, not the
                transparent square canvas around it. */}
            <img
              src="/pwa-512.png"
              alt="PointeTracer logo"
              style={{
                width: '280px',
                height: '280px',
                objectFit: 'contain',
                filter:
                  'drop-shadow(0 0 24px rgba(59,130,246,0.45)) drop-shadow(0 8px 24px rgba(0,0,0,0.35))',
              }}
            />

            {/* Tagline */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h1
                className="text-display-lg"
                style={{
                  color: 'var(--color-text-primary)',
                  margin: 0,
                  letterSpacing: '-0.5px',
                  whiteSpace: 'pre-line',
                }}
              >
                {tagline}
              </h1>
              <p
                className="text-body-md"
                style={{
                  color: 'var(--color-text-secondary)',
                  margin: 0,
                }}
              >
                Sign in to your PointeTracer account.
              </p>
            </div>
          </div>
        </div>

        {/* ── Right panel (form) ────────────────────────────────────────────── */}
        <div
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '32px 24px',
            backgroundColor: 'var(--color-surface-raised-2)',
          }}
          className="lg:w-1/2"
        >
          <div
            className="mc-login-form w-full"
            style={{ maxWidth: '400px' }}
          >
            {/* Form heading */}
            <div style={{ marginBottom: '40px' }}>
              {/* Mobile-only logo */}
              <div
                className="flex lg:hidden"
                style={{
                  justifyContent: 'center',
                  marginBottom: '32px',
                }}
              >
                <img
                  src="/pwa-512.png"
                  alt="PointeTracer logo"
                  style={{
                    width: '64px',
                    height: '64px',
                    objectFit: 'contain',
                    borderRadius: '16px',
                    boxShadow: '0 0 24px rgba(59,130,246,0.25)',
                  }}
                />
              </div>

              <h2
                className="text-display-lg"
                style={{
                  color: 'var(--color-text-primary)',
                  margin: '0 0 8px',
                  letterSpacing: '-0.5px',
                }}
              >
                Welcome back
              </h2>
              <p
                className="text-body-md"
                style={{
                  color: 'var(--color-text-secondary)',
                  margin: 0,
                }}
              >
                Sign in to continue.
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {/* Error banner */}
              {error && (
                <div
                  role="alert"
                  style={{
                    borderRadius: '10px',
                    border: '1px solid rgba(248, 113, 113, 0.30)',
                    backgroundColor: 'rgba(248, 113, 113, 0.08)',
                    padding: '12px 14px',
                    fontSize: '13px',
                    color: 'var(--color-signal-bad)',
                    lineHeight: 1.5,
                  }}
                >
                  {error}
                  {errorSuggestion && (
                    <p style={{ marginTop: '6px', fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: 0 }}>
                      {errorSuggestion}
                    </p>
                  )}
                </div>
              )}

              {/* Email field */}
              <div className="relative mc-input-wrap">
                <label
                  htmlFor="login-email"
                  className="text-label"
                  style={{
                    display: 'block',
                    color: 'var(--color-text-muted)',
                    marginBottom: '6px',
                  }}
                >
                  Email
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="email"
                    id="login-email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      height: '44px',
                      borderRadius: '10px',
                      border: '1px solid var(--color-border-strong)',
                      backgroundColor: 'var(--color-surface-raised-3)',
                      color: 'var(--color-text-primary)',
                      fontSize: '14px',
                      padding: '0 40px 0 14px',
                      boxSizing: 'border-box',
                      transition: 'border-color 180ms ease, box-shadow 180ms ease',
                    }}
                    aria-describedby={error ? 'login-error' : undefined}
                  />
                  <EnvelopeIcon
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '16px',
                      height: '16px',
                      color: 'var(--color-text-muted)',
                      pointerEvents: 'none',
                    }}
                    aria-hidden="true"
                  />
                </div>
              </div>

              {/* Password field */}
              <div className="relative mc-input-wrap">
                <label
                  htmlFor="login-password"
                  className="text-label"
                  style={{
                    display: 'block',
                    color: 'var(--color-text-muted)',
                    marginBottom: '6px',
                  }}
                >
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="login-password"
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isSubmitting}
                    style={{
                      width: '100%',
                      height: '44px',
                      borderRadius: '10px',
                      border: '1px solid var(--color-border-strong)',
                      backgroundColor: 'var(--color-surface-raised-3)',
                      color: 'var(--color-text-primary)',
                      fontSize: '14px',
                      padding: '0 40px 0 14px',
                      boxSizing: 'border-box',
                      transition: 'border-color 180ms ease, box-shadow 180ms ease',
                    }}
                  />
                  <PasswordToggleButton
                    visible={showPassword}
                    onToggle={() => setShowPassword((v) => !v)}
                    disabled={isSubmitting}
                    controls="login-password"
                  />
                </div>
              </div>

              {/* Submit button */}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full mc-submit-btn"
                style={{
                  borderRadius: '10px',
                  fontFamily: 'var(--font-display)',
                  fontWeight: 600,
                  fontSize: '15px',
                  letterSpacing: '0.1px',
                  marginTop: '4px',
                }}
                disabled={isSubmitting || !email || !password}
              >
                {isSubmitting ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span
                      style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        border: '2px solid rgba(6,9,15,0.30)',
                        borderTopColor: 'var(--color-text-inverse)',
                        animation: 'spin 0.75s linear infinite',
                        display: 'inline-block',
                        flexShrink: 0,
                      }}
                      aria-hidden="true"
                    />
                    Signing in...
                  </span>
                ) : (
                  'Sign in'
                )}
              </Button>

              {/* Forgot password */}
              <div style={{ textAlign: 'center' }}>
                <Link to="/auth/forgot-password" className="mc-forgot-link">
                  Forgot password?
                </Link>
              </div>
            </form>

            {/* Google Sign-In section — DORMANT when GOOGLE_CLIENT_ID is unset */}
            {GOOGLE_CLIENT_ID && (
              <div style={{ marginTop: '24px' }}>
                {/* Divider with "or" label */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px',
                  }}
                >
                  <div style={{ flex: 1, borderTop: '1px solid var(--color-border-subtle)' }} />
                  <span
                    className="text-body-sm"
                    style={{ color: 'var(--color-text-muted)', whiteSpace: 'nowrap', fontSize: '12px' }}
                  >
                    or
                  </span>
                  <div style={{ flex: 1, borderTop: '1px solid var(--color-border-subtle)' }} />
                </div>

                {/* Google error banner */}
                {error && googleErrorCode && (
                  <div
                    role="alert"
                    aria-live="polite"
                    style={{
                      borderRadius: '10px',
                      border: '1px solid rgba(248, 113, 113, 0.30)',
                      backgroundColor: 'rgba(248, 113, 113, 0.08)',
                      padding: '12px 14px',
                      fontSize: '13px',
                      color: 'var(--color-signal-bad)',
                      lineHeight: 1.5,
                      marginBottom: '12px',
                    }}
                  >
                    {error}
                    {googleErrorCode === 'account_not_found' && (
                      <p style={{ marginTop: '6px', fontSize: '12px', marginBottom: 0 }}>
                        Don&apos;t have an account yet?{' '}
                        <Link to="/request-access" className="mc-invite-link">
                          Request access
                        </Link>
                        .
                      </p>
                    )}
                  </div>
                )}

                {/* GSI-rendered button container. The GSI SDK renders its own
                    styled button into this div. Width is constrained to match
                    the form. Opacity reduced while a Google sign-in is in-flight. */}
                <div
                  ref={googleButtonRef}
                  aria-label="Sign in with Google"
                  style={{
                    width: '100%',
                    opacity: isGoogleSubmitting ? 0.6 : 1,
                    pointerEvents: isGoogleSubmitting ? 'none' : 'auto',
                    transition: 'opacity 150ms ease',
                    // Center the GSI-rendered button (it has a fixed pixel width)
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                />
              </div>
            )}

            {/* Divider + invitation copy */}
            <div style={{ marginTop: '32px' }}>
              <div
                style={{
                  borderTop: '1px solid var(--color-border-subtle)',
                  marginBottom: '20px',
                }}
              />
              <p
                className="text-body-sm"
                style={{
                  color: 'var(--color-text-muted)',
                  textAlign: 'center',
                  margin: 0,
                }}
              >
                Don&apos;t have an account?{' '}
                <Link to="/request-access" className="mc-invite-link">
                  Request access
                </Link>
              </p>
              <p
                className="text-body-sm"
                style={{
                  color: 'var(--color-text-muted)',
                  textAlign: 'center',
                  margin: '8px 0 0',
                  fontSize: '12px',
                }}
              >
                Need help?{' '}
                <a
                  href="mailto:hello@pointetracer.com"
                  style={{
                    color: 'var(--color-text-muted)',
                    textDecoration: 'underline',
                    fontWeight: 400,
                  }}
                >
                  Contact us
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Spinner keyframe — Button spinner uses global spin */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
}
